import {
  Box,
  Button,
  Divider,
  Stack,
  Tooltip,
  Typography,
  styled,
  useTheme,
} from "@mui/material"
import {
  Chip,
  SidebarCard,
  SidebarCardDetails,
  SidebarCardImage,
  type UploadedFile,
  getPublicFileUrl,
  labelOf,
} from "@pharmupp/p3-front-commons"
import type { PropsWithChildren } from "react"
import { Check, RefreshCw, X } from "react-feather"
import { OperationStateEnum } from ".."
import { ProofDocumentCount, StatusChip } from "../../commons"
import { marketingOpTypesEnum } from "../../marketingOperationReferential"
import { HiddenIconBtn } from "../common/components/HiddenBtn"
import {
  DocumentsGrid,
  QrCodeLink,
  SubmitDocumentsButton,
  SubscribeButton,
  TakePictureIcon,
} from "./components"
import {
  type ApiMarketingOperationDisplay,
  type MarketingOperationDisplay,
  useHideParticipationApi,
} from "./useAdhMarketingOPDisplayApi"

export function Sidebar({ operation }: { operation: MarketingOperationDisplay }) {
  const { palette } = useTheme()
  const fontColor = operation.hidden
    ? palette.action.disabled
    : marketingOpTypesEnum[operation.type].color || "primary.main"

  return (
    <SidebarCard gridArea="sidebar">
      {!!operation?.type && (
        <Chip
          size="small"
          label={labelOf(marketingOpTypesEnum, operation.type)}
          fontColor={fontColor}
          sx={{ fontSize: "0.6rem", mb: 2 }}
        />
      )}

      {!!operation && <SidebarImageAndState {...operation} />}

      <Divider sx={{ mt: 3 }} />

      <SidebarCardDetails phone="03 84 22 84 99" email="adv@pharm-upp.com" />
    </SidebarCard>
  )
}

const SidebarImageAndState = ({
  state,
  id,
  image,
  validityStartDate,
  validityEndDate,
  nbProofDocuments,
  proofDocuments,
  kitOrderEndDateFormatted,
  qrCodeToken,
  hidden,
}: MarketingOperationDisplay) => {
  const { palette } = useTheme()
  const operationId = id.toString()
  const { hideParticipation } = useHideParticipationApi({ operationId })

  switch (state) {
    case OperationStateEnum.UPCOMING:
      return (
        <SidebarImageAndStateLayout image={image}>
          <Stack direction="row" gap={0.5}>
            <SubscribeButton operationId={operationId} hidden={hidden} disabled />
            <HiddenIconBtn
              hidden={hidden}
              size="large"
              handleClick={hideParticipation}
            />
          </Stack>
        </SidebarImageAndStateLayout>
      )
    case OperationStateEnum.WAITING:
      return (
        <SidebarImageAndStateLayout
          image={image}
          dateLabel={`Lancement le ${validityStartDate}`}
        >
          <Tooltip title="L'envoi du justificatif ne sera possible que lorsque l'opération aura démarré">
            <span>
              <Button variant="contained" sx={{ color: "white!important" }} disabled>
                Soumettre justificatif
              </Button>
            </span>
          </Tooltip>
        </SidebarImageAndStateLayout>
      )
    case OperationStateEnum.EXPIRED:
      return (
        <SidebarImageAndStateLayout image={image}>
          <Tooltip
            title={`L'opération est maintenant terminée depuis le ${validityEndDate}`}
          >
            <StatusChip
              label="Opération clôturée"
              icon={<X />}
              color={palette.common.red}
            />
          </Tooltip>
        </SidebarImageAndStateLayout>
      )
    case OperationStateEnum.OUTDATED:
      return (
        <SidebarImageAndStateLayout image={image}>
          <Tooltip
            title={`L'opération est maintenant terminée depuis le ${kitOrderEndDateFormatted}`}
          >
            <StatusChip
              label="Opération clôturée"
              icon={<X />}
              color={palette.common.red}
            />
          </Tooltip>
        </SidebarImageAndStateLayout>
      )
    case OperationStateEnum.STARTED:
      return (
        <SidebarImageAndStateLayout image={image}>
          <Stack spacing={2} width="100%">
            <ProofDocumentCount
              count={nbProofDocuments}
              size="small"
              sx={{ width: "100%" }}
            />
            <SubmitDocumentsButton operationId={operationId}>
              Soumettre justificatif
            </SubmitDocumentsButton>

            {!!qrCodeToken && <QrCodeBlock qrCodeToken={qrCodeToken} />}
          </Stack>
        </SidebarImageAndStateLayout>
      )
    case OperationStateEnum.REVIEWED:
      return (
        <SidebarImageAndStateLayout image={image}>
          <SubmittedDocuments proofDocuments={proofDocuments} />
          <StatusChip
            label="Justificatif transmis"
            icon={<RefreshCw />}
            color={palette.success.main}
          />
        </SidebarImageAndStateLayout>
      )
    case OperationStateEnum.REJECTED:
      return (
        <SidebarImageAndStateLayout image={image}>
          <Stack spacing={2} width="100%">
            <SubmittedDocuments proofDocuments={proofDocuments} />
            <StatusChip
              label="Justificatif refusé"
              icon={<X />}
              color={palette.error.main}
            />
            <SubmitDocumentsButton operationId={operationId}>
              Nouveau justificatif
            </SubmitDocumentsButton>

            {!!qrCodeToken && <QrCodeBlock qrCodeToken={qrCodeToken} />}
          </Stack>
        </SidebarImageAndStateLayout>
      )
    case OperationStateEnum.VALIDATED:
      return (
        <SidebarImageAndStateLayout image={image}>
          <SubmittedDocuments proofDocuments={proofDocuments} />
          <StatusChip
            label="Opération validée"
            icon={<Check />}
            color={palette.success.main}
          />
        </SidebarImageAndStateLayout>
      )
    default:
      return (
        <SidebarImageAndStateLayout image={image}>
          <Stack direction="row" alignItems="center" gap={1}>
            <SubscribeButton operationId={operationId} hidden={hidden} />
            <HiddenIconBtn
              hidden={hidden}
              size="large"
              handleClick={hideParticipation}
            />
          </Stack>
        </SidebarImageAndStateLayout>
      )
  }
}

const SidebarImageAndStateLayout = ({
  image,
  dateLabel,
  children,
}: PropsWithChildren<{ image?: UploadedFile | null; dateLabel?: string }>) => {
  const { typography } = useTheme()
  return (
    <Box>
      <Box display="flex" flexDirection="column" mb={2}>
        {/* IMAGE */}
        <SidebarCardImage
          src={image ? getPublicFileUrl(image) : "/news-placeholder.png"}
        />
        {/* DATE */}
        {!!dateLabel && (
          <Box
            width="100%"
            color="primary.main"
            bgcolor="#d8e2f4"
            borderRadius="0 0 7px 7px"
            fontSize={typography.pxToRem(12)}
            lineHeight={typography.pxToRem(22)}
            textAlign="center"
          >
            {dateLabel}
          </Box>
        )}
      </Box>

      <Stack alignItems="flex-start" spacing={2}>
        {children}
      </Stack>
    </Box>
  )
}

const SubmittedDocuments = ({
  proofDocuments,
}: {
  proofDocuments: ApiMarketingOperationDisplay["proofDocuments"]
}) => {
  return (
    <Stack spacing={1}>
      <Typography
        fontSize={(theme) => theme.typography.pxToRem(16)}
        fontWeight={600}
        color="common.darkBlue"
      >
        {proofDocuments?.length}{" "}
        {(proofDocuments?.length ?? 0) > 1 ? "justificatifs" : "justificatif"} soumis
      </Typography>
      <DocumentsGrid proofDocuments={proofDocuments} />
    </Stack>
  )
}

const OrBox = styled(Box)(({ theme: { palette, typography } }) => ({
  border: `1px solid ${palette.grey.ultraLight}`,
  borderRadius: "100px",
  width: "32px",
  height: "32px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  fontSize: typography.pxToRem(12),
  color: palette.common.darkBlue,
}))

const QrCodeBlock = ({ qrCodeToken }: { qrCodeToken: string }) => {
  const { typography } = useTheme()
  return (
    <>
      <Divider>
        <OrBox>ou</OrBox>
      </Divider>

      <Stack direction="row" gap={1.5}>
        <TakePictureIcon />
        <Stack color="common.darkBlue">
          <Typography fontWeight={600} fontSize={typography.pxToRem(14)}>
            Justifier via mobile
          </Typography>
          <Typography
            lineHeight={typography.pxToRem(13)}
            fontSize={typography.pxToRem(11)}
          >
            Scanner le code pour transmettre vos justificatifs
          </Typography>
        </Stack>
      </Stack>

      <Stack alignItems="center">
        <QrCodeLink link={`${window.CONTEXT.justifyOpeAppUrl}/${qrCodeToken}`} />
      </Stack>
    </>
  )
}
